import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { createFileRoute, Link } from '@tanstack/react-router'
import { useTranslations } from 'gt-react'
import { Loader2, Pencil, Plus, Trash2, Users } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Textarea } from '@/components/ui/textarea'
import { orpc } from '@/lib/orpcClient'

export const Route = createFileRoute('/settings/teams/')({
  component: TeamsListPage,
})

interface TeamSummary {
  id: string
  name: string
  description: string | null
  memberCount: number
  createdAt: Date
  updatedAt: Date
}

function TeamsListPage() {
  const t = useTranslations()
  const qc = useQueryClient()
  const listQ = useQuery(orpc.rbac.teams.list.queryOptions())
  const teams = (listQ.data ?? []) as TeamSummary[]

  const [createOpen, setCreateOpen] = useState(false)
  const [form, setForm] = useState({ name: '', description: '' })
  const [deleteDialog, setDeleteDialog] = useState<{
    open: boolean
    id: string | null
    name: string
  }>({ open: false, id: null, name: '' })

  const invalidate = () => qc.invalidateQueries({ queryKey: orpc.rbac.teams.list.key() })

  const createMut = useMutation(
    orpc.rbac.teams.create.mutationOptions({
      onSuccess: () => {
        toast.success(t('rbac.teams.created') || 'Team created')
        setCreateOpen(false)
        setForm({ name: '', description: '' })
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const deleteMut = useMutation(
    orpc.rbac.teams.delete.mutationOptions({
      onSuccess: () => {
        toast.success(t('rbac.teams.deleted') || 'Team deleted')
        setDeleteDialog({ open: false, id: null, name: '' })
        invalidate()
      },
      onError: (e: Error) => toast.error(e.message),
    }),
  )

  const canCreate = form.name.trim().length > 0

  return (
    <div className="container mx-auto py-6 px-4 max-w-5xl">
      <div className="mb-6 flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">{t('rbac.teams.title') || 'Teams'}</h1>
          <p className="text-muted-foreground">
            {t('rbac.teams.subtitle') ||
              'Groups of users used by scope="team" role grants for row-level visibility'}
          </p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          {t('rbac.teams.new') || 'New team'}
        </Button>
      </div>

      <Card className="mb-6 border-dashed">
        <CardHeader className="flex flex-row items-start gap-4 space-y-0">
          <Users className="mt-1 h-5 w-5 shrink-0 text-muted-foreground" />
          <div className="flex-1">
            <CardTitle className="text-base">
              {t('rbac.teams.aboutTitle') || 'How teams are used'}
            </CardTitle>
            <CardDescription>
              {t('rbac.teams.aboutBody') ||
                'Teams do not grant permissions. They define a set of peers whose records a team-scoped role grant will show. Example: a sales_manager role with `scope=team` on CRM opportunities shows every opportunity owned by anyone on the manager\u2019s team.'}
            </CardDescription>
          </div>
        </CardHeader>
      </Card>

      <Card>
        <CardContent className="p-0">
          {listQ.isLoading ? (
            <div className="p-6 space-y-2">
              {[...Array(3)].map((_, i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : teams.length === 0 ? (
            <div className="p-12 text-center text-muted-foreground">
              {t('rbac.teams.empty') || 'No teams yet. Create one to start grouping users.'}
            </div>
          ) : (
            <table className="w-full">
              <thead>
                <tr className="border-b">
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('rbac.teams.name') || 'Name'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('rbac.teams.description') || 'Description'}
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('rbac.teams.members') || 'Members'}
                  </th>
                  <th className="px-6 py-3 text-right text-xs font-medium uppercase tracking-wider text-muted-foreground">
                    {t('rbac.teams.actions') || 'Actions'}
                  </th>
                </tr>
              </thead>
              <tbody>
                {teams.map((team) => (
                  <tr key={team.id} className="border-b last:border-b-0 hover:bg-muted/30">
                    <td className="px-6 py-3 font-medium">
                      <Link
                        to="/settings/teams/$teamId"
                        params={{ teamId: team.id }}
                        className="hover:underline"
                      >
                        {team.name}
                      </Link>
                    </td>
                    <td className="px-6 py-3 text-sm text-muted-foreground">
                      {team.description ?? '\u2014'}
                    </td>
                    <td className="px-6 py-3">
                      <Badge variant="secondary">{team.memberCount}</Badge>
                    </td>
                    <td className="px-6 py-3 text-right">
                      <Button asChild variant="ghost" size="sm">
                        <Link to="/settings/teams/$teamId" params={{ teamId: team.id }}>
                          <Pencil className="h-3.5 w-3.5" />
                        </Link>
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() =>
                          setDeleteDialog({ open: true, id: team.id, name: team.name })
                        }
                      >
                        <Trash2 className="h-3.5 w-3.5 text-destructive" />
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </CardContent>
      </Card>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('rbac.teams.createTitle') || 'Create team'}</DialogTitle>
            <DialogDescription>
              {t('rbac.teams.createBody') || 'Pick a descriptive name. Members can be added next.'}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="team-name">{t('rbac.teams.name') || 'Name'}</Label>
              <Input
                id="team-name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Sales West"
                maxLength={128}
              />
            </div>
            <div>
              <Label htmlFor="team-desc">{t('rbac.teams.description') || 'Description'}</Label>
              <Textarea
                id="team-desc"
                value={form.description}
                onChange={(e) => setForm({ ...form, description: e.target.value })}
                placeholder={t('rbac.teams.descPlaceholder') || 'Optional'}
                maxLength={2000}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>
              {t('common.cancel') || 'Cancel'}
            </Button>
            <Button
              disabled={!canCreate || createMut.isPending}
              onClick={() =>
                createMut.mutate({
                  name: form.name.trim(),
                  ...(form.description.trim() ? { description: form.description.trim() } : {}),
                })
              }
            >
              {createMut.isPending ? (
                <Loader2 className="h-4 w-4 animate-spin" />
              ) : (
                t('rbac.teams.create') || 'Create'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlertDialog
        open={deleteDialog.open}
        onOpenChange={(open) => setDeleteDialog((d) => ({ ...d, open }))}
      >
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>
              {t('rbac.teams.deleteTitle') || 'Delete team'} "{deleteDialog.name}"?
            </AlertDialogTitle>
            <AlertDialogDescription>
              {t('rbac.teams.deleteBody') ||
                'All memberships are removed. Users on team-scoped grants will stop seeing records owned by teammates — the grant itself is not modified.'}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('common.cancel') || 'Cancel'}</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground"
              onClick={() => deleteDialog.id && deleteMut.mutate({ id: deleteDialog.id })}
            >
              {t('rbac.teams.delete') || 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  )
}
